import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import DataAnalysisFilters from '../../components/DataAnalysisFilters/DataAnalysisFilters'
import { ChartExportWrapper, StackedBarChart, ToggleButton } from 'components'
import { getTabByKey, prettyAttr, TAB_KEYS } from '../../utils/consts'
import {
  selectChartData,
  selectChartStatus,
} from 'store/selectors/dataAnalysisSelectors'
import { applyAndFetchChart } from 'store/slices/dataAnalysisSlice'
import EmptyState from 'pages/DataAnalysis/components/EmptyState'
import { TBinDistributionData } from 'types/models/DataAnalysis'
import { toBinDistributionChartData } from '../../transforms'
import * as sxStyles from '../tabs.styles'
import { DataTabsLayout, TrendChartStatus } from '../../layouts'
import normalizeDataset from '../../utils/normalizeDataset'

export const BinDistribution = () => {
  const dispatch = useDispatch()
  const rawData = useSelector(selectChartData(TAB_KEYS.BIN_DISTRIBUTION))
  const status = useSelector(selectChartStatus(TAB_KEYS.BIN_DISTRIBUTION))
  const currentTab = getTabByKey(TAB_KEYS.BIN_DISTRIBUTION)

  const [mode, setMode] = useState<'count' | 'percentage'>('percentage')

  const data = useMemo(
    () => normalizeDataset<TBinDistributionData>(rawData as any),
    [rawData]
  )
  const { rows, series } = useMemo(
    () => toBinDistributionChartData(data, mode),
    [data, mode]
  )

  const hasAnyData = rows.length > 0

  const cleanedRows = rows.map((item) => ({
    ...item,
    wheatLabel: item.wheatLabel.replace(/^Wheat\s*/i, ''),
  }))

  return (
    <DataTabsLayout
      sidebar={
        <DataAnalysisFilters
          filtersList={currentTab?.filters ?? []}
          chartKey={TAB_KEYS.BIN_DISTRIBUTION}
        />
      }
      tabKey={TAB_KEYS.BIN_DISTRIBUTION}
      onApply={() =>
        dispatch(applyAndFetchChart({ key: TAB_KEYS.BIN_DISTRIBUTION }))
      }
    >
      <TrendChartStatus status={status} title={TAB_KEYS.BIN_DISTRIBUTION}>
        {!hasAnyData ? (
          <EmptyState />
        ) : (
          <Box display="flex" justifyContent="center" width="100%">
            <Card
              data-exportable-chart="true"
              variant="outlined"
              sx={{
                ...sxStyles.parametersComparisonCard,
                width: '100%',
              }}
            >
              <CardHeader
                title={prettyAttr('Bin Distribution')}
                className="no-print"
                titleTypographyProps={{ fontSize: 24, fontWeight: 800 }}
                action={
                  <ToggleButtonGroup
                    size="small"
                    value={mode}
                    exclusive
                    onChange={(_, val) => val && setMode(val)}
                  >
                    <Tooltip title="Raw Count">
                      <span>
                        <ToggleButton value="count">#</ToggleButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Percentage">
                      <span>
                        <ToggleButton value="percentage">%</ToggleButton>
                      </span>
                    </Tooltip>
                  </ToggleButtonGroup>
                }
              />
              <CardContent
                sx={{
                  ...sxStyles.parametersComparisonCardContent,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <ChartExportWrapper
                    title="Bin Distribution"
                    actionsRightPadding={48}
                  >
                    <StackedBarChart
                      data={cleanedRows}
                      categoryKey="wheatLabel"
                      series={series}
                      percentage={mode === 'percentage'}
                      tooltipKey="spread_score"
                      tooltipRender={(v) => <div>Spread score: {v}</div>}
                      minLabelValue={1}
                      orientation="horizontal"
                      hideXAxisLabels
                    />
                  </ChartExportWrapper>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </TrendChartStatus>
    </DataTabsLayout>
  )
}
